import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
} from "@angular/core";
import {
  FormControl,
  FormGroupDirective,
  NgForm,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
} from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import {
  distinctUntilChanged,
  takeUntil,
  debounceTime,
  filter,
  tap,
  delay,
} from "rxjs/operators";
import { Store } from "@ngrx/store";
import { Subject, pipe } from "rxjs";
import {
  selectAddOrganizationForm,
  selectAddOrganizationFilteredStates,
  selectAddOrganizationFilteredTypes,
} from "@store/selectors/add-organization/add-organization.selectors";
import { CommonModule } from "@angular/common";
import { ConnectFormDirective } from "@directives/connect-form/connect-form.directive";
import { AddOrganizationActions } from "@store/actions/add-organization/add-organization.actions";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { MatDialog } from "@angular/material/dialog";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { UserService } from "@services/user.service";
import { RoleConstants, ROLE_GROUPS } from "../../constants/roles";
import { MatIconModule } from "@angular/material/icon";
import { EditAddOrganizationModalComponent } from "@components/modals/edit-add-organization-modal/edit-add-organization-modal.component";
import { selectIsTestOrganization, selectOrganizationID } from "@store/selectors/add-organization/add-organization.selectors";
import { OrganizationAdminActions } from "@store/actions/organization-admin/organization-admin.actions";
import { ReportingActions } from "@store/actions/reporting/reporting.actions";
import { selectIsExportingAudit } from "@store/selectors/reporting/reporting.selectors";

@Component({
  selector: "app-create-organization-details",
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatCardModule,
    CommonModule,
    ConnectFormDirective,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: "./create-organization-details.component.html",
  styleUrl: "./create-organization-details.component.css",
})
export class CreateOrganizationDetailsComponent
  implements OnInit, OnChanges, OnDestroy
{
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly userService = inject(UserService);
  private ngUnsubscribe = new Subject<void>();
  private readonly debounceTime = 200;
  private readonly selectDebounceTime = 100;

  private readonly standardFormPipe = pipe(
    distinctUntilChanged<string>(),
    takeUntil(this.ngUnsubscribe)
  );

  private readonly filterSelectPipe = pipe(
    distinctUntilChanged<string>(),
    takeUntil(this.ngUnsubscribe)
  );

  parentForm!: FormGroup;
  private readonly ctrlContainer = inject(FormGroupDirective);
  private readonly fb = inject(FormBuilder);

  isSuperAdmin$ = this.userService.hasRequiredRoles(ROLE_GROUPS.SUPER_USER);
  isExportingAudit$ = this.store.select(selectIsExportingAudit);

  form$ = this.store.select(selectAddOrganizationForm);
  isTestOrganization$ = this.store.select(selectIsTestOrganization);
  currentOrganizationId = '';
  statesDropdown$ = this.store.select(selectAddOrganizationFilteredStates);
  organizationTypesDropdown$ = this.store.select(
    selectAddOrganizationFilteredTypes
  );

  createOrganizationForm: FormGroup = new FormGroup({
    name: new FormControl("", {
      validators: [Validators.required, Validators.maxLength(256)],
    }),
    typeSearch: new FormControl(""),
    type: new FormControl("", {
      validators: [Validators.required],
    }),
    organizationID: new FormControl({ value: "", disabled: true }),
    legacyOrganizationID: new FormControl("", {
      validators: [Validators.required, Validators.maxLength(24)],
    }),
    crmID: new FormControl("", {
      validators: [Validators.required, Validators.maxLength(24)],
    }),
    address: new FormControl("", {
      validators: [Validators.required, Validators.maxLength(50)],
    }),
    addressB: new FormControl("", {
      validators: [Validators.maxLength(50)],
    }),
    zipCode: new FormControl("", {
      validators: [Validators.required, Validators.maxLength(15)],
    }),
    city: new FormControl("", {
      validators: [Validators.required, Validators.maxLength(35)],
    }),
    stateSearch: new FormControl(""),
    state: new FormControl("", {
      validators: [Validators.required],
    }),
  });

  ngOnInit() {
    this.store.dispatch(AddOrganizationActions.pageLoaded());

    this.store.select(selectOrganizationID)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((id) => { this.currentOrganizationId = id; });

    this.createOrganizationForm.controls["name"].valueChanges
      .pipe(this.standardFormPipe)
      .subscribe((value: string) => {
        this.store.dispatch(AddOrganizationActions.nameChanged({ value }));
      });

    this.createOrganizationForm.controls["type"].valueChanges
      .pipe(this.standardFormPipe)
      .subscribe((value: string) => {
        this.store.dispatch(
          AddOrganizationActions.typeChanged({ value: +value })
        );
      });

    this.createOrganizationForm.controls["legacyOrganizationID"].valueChanges
      .pipe(this.standardFormPipe)
      .subscribe((value: string) => {
        this.store.dispatch(AddOrganizationActions.legacyIDChanged({ value }));
      });

    this.createOrganizationForm.controls["crmID"].valueChanges
      .pipe(this.standardFormPipe)
      .subscribe((value: string) => {
        this.store.dispatch(AddOrganizationActions.cRMIDChanged({ value }));
      });

    this.createOrganizationForm.controls["address"].valueChanges
      .pipe(this.standardFormPipe)
      .subscribe((value: string) => {
        this.store.dispatch(AddOrganizationActions.addressChanged({ value }));
      });

    this.createOrganizationForm.controls["addressB"].valueChanges
      .pipe(this.standardFormPipe)
      .subscribe((value: string) => {
        this.store.dispatch(AddOrganizationActions.addressBChanged({ value }));
      });

    this.createOrganizationForm.controls["zipCode"].valueChanges
      .pipe(this.standardFormPipe)
      .subscribe((value: string) => {
        this.store.dispatch(AddOrganizationActions.zipCodeChanged({ value }));
      });

    this.createOrganizationForm.controls["city"].valueChanges
      .pipe(this.standardFormPipe)
      .subscribe((value: string) => {
        this.store.dispatch(AddOrganizationActions.cityChanged({ value }));
      });

    this.createOrganizationForm.controls["state"].valueChanges
      .pipe(this.standardFormPipe)
      .subscribe((value: any) => {
        this.store.dispatch(
          AddOrganizationActions.stateChanged({ value: +value })
        );
      });

    this.createOrganizationForm.controls["typeSearch"].valueChanges
      .pipe(this.filterSelectPipe)
      .subscribe((value: string) => {
        this.store.dispatch(
          AddOrganizationActions.typeSearchChanged({ value })
        );
      });

    this.createOrganizationForm.controls["stateSearch"].valueChanges
      .pipe(this.filterSelectPipe)
      .subscribe((value: string) => {
        this.store.dispatch(
          AddOrganizationActions.stateSearchChanged({ value })
        );
      });

    this.createOrganizationForm.statusChanges
      .pipe(this.standardFormPipe)
      .subscribe((status) => {
        this.store.dispatch(
          AddOrganizationActions.validityChanged({
            validity: status === "VALID",
          })
        );
      });

    this.parentForm = this.ctrlContainer.form;
    this.parentForm.addControl("orgInfo", this.createOrganizationForm);
  }

  ngOnChanges(changes: SimpleChanges) {}

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onTestOrgToggle(checked: boolean): void {
    const orgId = this.currentOrganizationId;
    if (!orgId) return;
    this.store.dispatch(OrganizationAdminActions.setTestOrganizationStatus({
      organizationId: orgId,
      isTestOrganization: checked ? 1 : 0,
    }));
  }

  onExportOrgAudit(): void {
    this.store.dispatch(ReportingActions.exportOrgAudit());
  }

  addNewOrganization(): void {
    const dialogRef = this.dialog.open(EditAddOrganizationModalComponent, {
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((organizationData) => {
      if (organizationData) {
        this.store.dispatch(
          AddOrganizationActions.createNewOrganization({
            organizationData,
          })
        );
      }
    });
  }
}
